import { useState } from "react";
import { AlertTriangle, Gem, Briefcase, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Subscription } from "@/api/billing-api";
import { useOrgQuota } from "@/hooks/use-org-quota";
import { orgBillingApi, type OrgQuota } from "@/api/org-billing-api";
import { getErrorMessage } from "@/lib/api-error";

// incomplete_expired is intentionally excluded: once Stripe expires an
// unconfirmed subscription, updating the payment method via a SetupIntent
// cannot revive it — the user has to re-checkout the plan. We let those
// orgs fall through to the regular "no active subscription" path instead.
const PAYMENT_FAILURE_STATUSES = new Set([
  "past_due",
  "unpaid",
  "incomplete",
]);

function deriveCadence(interval?: string, count?: number): string {
  if (!interval || !count) return "Monthly";
  if (interval === "year") return "Annual";
  if (interval === "month") {
    if (count === 1) return "Monthly";
    if (count === 3) return "Quarterly";
    if (count === 6) return "Semi-Annual";
    if (count === 12) return "Annual";
  }
  return "Monthly";
}

function formatRenewalDate(unixSeconds?: number): string {
  if (!unixSeconds) return "—";
  return new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatCurrency(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

// Stripe price nicknames are free-form and often lowercase (e.g. "elite
// plan"); title-case them for display so the plan name reads consistently
// regardless of how it's configured in Stripe.
function titleCase(input: string): string {
  return input
    .split(" ")
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(" ");
}

export default function ActiveBilling({
  subscription,
  orgId,
}: {
  subscription: Subscription;
  orgId?: string;
}) {
  const item = subscription.items[0];
  const planName = item?.nickname ? titleCase(item.nickname) : "Current plan";
  const cadence = deriveCadence(item?.recurring.interval, item?.recurring.interval_count);
  const renewalDate = formatRenewalDate(item?.current_period_end);
  const renewalAmount = formatCurrency(item?.amount ?? 0);

  const monthsInCycle =
    item?.recurring.interval === "year"
      ? 12
      : item?.recurring.interval_count ?? 1;
  const monthlyDisplay = formatCurrency(((item?.amount ?? 0) / monthsInCycle));

  const { quota, isLoading: isQuotaLoading, error: quotaError } = useOrgQuota();

  const handleManage = () => {
    document.getElementById("plan-grid")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      {PAYMENT_FAILURE_STATUSES.has(subscription.status) && (
        <PaymentFailureBanner status={subscription.status} orgId={orgId} />
      )}

      <RenewalBanner
        cadence={cadence}
        date={renewalDate}
        amount={renewalAmount}
        onManage={handleManage}
      />

      <section className="grid gap-4 md:grid-cols-3">
        <SummaryCard
          label="Current plan"
          title={planName}
          subtitle={`${monthlyDisplay}/mo · billed ${cadence.toLowerCase()}`}
          meta={`Renews ${renewalDate}`}
          actions={
            <Button size="sm" onClick={handleManage}>
              Manage
            </Button>
          }
        />

        {isQuotaLoading ? (
          <>
            <UsageCardSkeleton
              label="Premium deals, shared org-wide"
              icon={<Gem className="h-4 w-4 text-muted-foreground" />}
            />
            <UsageCardSkeleton
              label="Workspaces"
              icon={<Briefcase className="h-4 w-4 text-muted-foreground" />}
            />
          </>
        ) : quotaError || !quota ? (
          <UsageCardError className="md:col-span-2" />
        ) : (
          <>
            <PremiumDealsCard quota={quota} />

            <UsageCard
              label="Workspaces"
              icon={<Briefcase className="h-4 w-4 text-muted-foreground" />}
              used={quota.workspaces_used}
              cap={quota.workspaces_cap}
              captionFn={() =>
                `${Math.max(0, quota.workspaces_cap - quota.workspaces_used)} slot available`
              }
            />
          </>
        )}
      </section>
    </>
  );
}

function PaymentFailureBanner({
  status,
  orgId,
}: {
  status: string;
  orgId?: string;
}) {
  const [isUpdating, setIsUpdating] = useState(false);

  const isIncomplete = status === "incomplete";
  const title = isIncomplete
    ? "Action required to activate subscription"
    : "Payment failed";
  const body = isIncomplete
    ? "Your subscription isn't active yet — your last payment needs to be confirmed. Update or confirm your payment method to finish activating it."
    : "We couldn't complete your last payment. Update your payment method to keep your subscription active and avoid losing premium access.";

  const handleUpdate = async () => {
    if (!orgId) {
      toast.error("No organization context");
      return;
    }
    setIsUpdating(true);
    try {
      const returnUrl = `${window.location.origin}${window.location.pathname}`;
      const session = await orgBillingApi.startPaymentMethodSetup(
        orgId,
        `${returnUrl}?setup_session={CHECKOUT_SESSION_ID}`,
        returnUrl
      );
      window.location.href = session.checkout_url;
    } catch (err) {
      console.error("Card update failed", err);
      toast.error(getErrorMessage(err, "Couldn't start card update"));
      setIsUpdating(false);
    }
  };

  return (
    <div
      role="alert"
      className="flex flex-col gap-3 rounded-lg border border-destructive/50 bg-destructive/10 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
    >
      <div className="flex items-start gap-3 min-w-0">
        <AlertTriangle
          className="h-5 w-5 shrink-0 text-destructive mt-0.5"
          aria-hidden="true"
        />
        <div className="min-w-0">
          <h4 className="text-sm font-medium text-destructive">{title}</h4>
          <p className="mt-1 text-sm text-foreground">{body}</p>
        </div>
      </div>
      <Button
        size="sm"
        variant="destructive"
        onClick={handleUpdate}
        disabled={isUpdating || !orgId}
        className="w-full shrink-0 sm:w-auto"
      >
        {isUpdating ? (
          <>
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Redirecting…
          </>
        ) : (
          "Update payment method"
        )}
      </Button>
    </div>
  );
}

function RenewalBanner({
  cadence,
  date,
  amount,
  onManage,
}: {
  cadence: string;
  date: string;
  amount: string;
  onManage: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-muted/30 px-5 py-3">
      <p className="text-sm text-foreground">
        Your {cadence.toLowerCase()} cycle renews on{" "}
        <span className="font-medium">{date}</span> for{" "}
        <span className="font-medium">{amount}</span>.
      </p>
      <button
        type="button"
        onClick={onManage}
        className="shrink-0 text-sm font-medium text-foreground underline-offset-4 hover:underline"
      >
        Manage renewal →
      </button>
    </div>
  );
}

function SummaryCard({
  label,
  title,
  subtitle,
  meta,
  actions,
}: {
  label: string;
  title: string;
  subtitle: string;
  meta: string;
  actions: React.ReactNode;
}) {
  return (
    <div className="flex flex-col rounded-lg border border-border bg-card p-5">
      <div className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground/70">
        {label}
      </div>
      <h3 className="mt-2 text-lg font-semibold text-foreground">{title}</h3>
      <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
      <p className="mt-3 text-xs text-muted-foreground">{meta}</p>
      <div className="mt-auto pt-4 flex gap-2">{actions}</div>
    </div>
  );
}

export function PremiumDealsCard({ quota }: { quota: OrgQuota }) {
  return (
    <UsageCard
      label="Premium deals, shared org-wide"
      icon={<Gem className="h-4 w-4 text-muted-foreground" />}
      used={quota.premium_deals_used}
      cap={quota.premium_deals_cap}
      captionFn={(remaining) =>
        quota.add_on_credits_balance > 0
          ? `${remaining} remaining + ${quota.add_on_credits_balance} add-on credit${
              quota.add_on_credits_balance === 1 ? "" : "s"
            }`
          : `${remaining} remaining; once premium, all workspaces always premium`
      }
    />
  );
}

function UsageCard({
  label,
  icon,
  used,
  cap,
  captionFn,
}: {
  label: string;
  icon: React.ReactNode;
  used: number;
  cap: number;
  captionFn: (remaining: number) => string;
}) {
  const remaining = Math.max(0, cap - used);
  const pct = cap > 0 ? Math.min(100, (used / cap) * 100) : 0;

  return (
    <div className="flex flex-col rounded-lg border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-widest text-muted-foreground/70">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-2xl font-semibold text-foreground">{used}</span>
        <span className="text-sm text-muted-foreground">/ {cap}</span>
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full bg-foreground transition-all")}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="mt-3 text-xs text-muted-foreground">{captionFn(remaining)}</p>
    </div>
  );
}

export function UsageCardSkeleton({
  label,
  icon,
}: {
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="flex flex-col rounded-lg border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-widest text-muted-foreground/70">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-6 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    </div>
  );
}

export function UsageCardError({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card p-5 text-center",
        className
      )}
    >
      <p className="text-sm font-medium text-foreground">Usage unavailable</p>
      <p className="mt-1 text-xs text-muted-foreground">
        Couldn&apos;t load org-wide usage. Refresh to retry.
      </p>
    </div>
  );
}
